import { jsonRequest, request, socialUrl } from './http.js';
import type { SocialResult } from './http.js';
import { toFriend, toList, toSearchResult, toVoid, toWrapped } from './decode.js';
import type { Friend, FriendSearchResult } from './types.js';

/** `POST /friends/add` takes exactly one of the two — both, or neither, is 400. */
export type AddFriendTarget = { userId: string } | { handle: string };

export function listFriends(): Promise<SocialResult<Friend[]>> {
  return request(socialUrl('/friends/list'), { method: 'GET' }, (body) =>
    toList(body, 'friends', toFriend)
  );
}

/**
 * A query below the server's minimum length is not an error: the Worker answers
 * 200 with an empty list so the UI renders "too short" and "no matches"
 * identically while the user types. The minimum lives server-side only —
 * duplicating it here would drift.
 */
export function searchFriends(query: string): Promise<SocialResult<FriendSearchResult[]>> {
  return request(socialUrl('/friends/search', { q: query }), { method: 'GET' }, (body) =>
    toList(body, 'results', toSearchResult)
  );
}

/** Idempotent: adding someone you already follow succeeds with the same body. */
export function addFriend(target: AddFriendTarget): Promise<SocialResult<Friend>> {
  return jsonRequest(socialUrl('/friends/add'), 'POST', target, (body) =>
    toWrapped(body, 'friend', toFriend)
  );
}

/** Also idempotent — removing someone you never followed succeeds. */
export function removeFriend(userId: string): Promise<SocialResult<void>> {
  // Query parameter, not a body: DELETE bodies are unevenly handled by
  // intermediaries, which is why the Worker reads `?userId=`.
  return request(socialUrl('/friends/remove', { userId }), { method: 'DELETE' }, toVoid);
}
